<template>
  <div class="chart-container" :style="{ width: width }">
    <bar-chart :chart-data="chartData" :options="chartOptions" />
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { localeString } from '@/config/helpers.js'
import BarChart from '@/components/BarChart.vue'
import { splitUnitName, filterUnit, removeCommonElement } from '@/modules/industria/helpers/chartHelper.js'

export default {
  name: 'BenefitValuesChart',
  components: {
    BarChart,
  },
  props: {
    selectedUnits: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      default(_) {
        return []
      },
    },
    offerTypeName: {
      type: String,
      required: true,
    },
    offerTypeNameCapital: {
      type: String,
      required: true,
    },
    sorting: {
      type: String,
      default: 'bg-desc',
    },
    hideEarn: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    chartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
              callback: (value, index, values) => {
                return 'R$ ' + localeString(value)
              },
            },
          },
        ],
        xAxes: [
          {
            gridLines: {
              display: false,
            },
          },
        ],
      },
      legend: {
        display: false,
      },
      tooltips: {
        callbacks: {
          title: (tooltipItem, data) => {
            return tooltipItem[0].xLabel.join(' ')
          },
          label: (tooltipItem, data) => {
            return `${data.datasets[tooltipItem.datasetIndex].label}: R$ ${localeString(tooltipItem.yLabel, 2)}`
          },
        },
      },
      layout: {
        padding: {
          top: 30,
        },
      },
      plugins: {
        datalabels: {
          clamp: true,
          anchor: 'end',
          align: 'top',
          offset: 0,
          formatter: (x) => 'R$ ' + localeString(x),
        },
      },
    },
  }),
  computed: {
    chartData() {
      let itemsClone = cloneDeep(this.items)
      const units = []
      const earnValue = []
      const burnValue = []

      switch (this.sorting) {
        case 'bg-asc':
          itemsClone.sort((a, b) => a.earn_value - b.earn_value)
          break
        case 'bg-desc':
          itemsClone.sort((a, b) => b.earn_value - a.earn_value)
          break
        case 'bc-asc':
          itemsClone.sort((a, b) => a.burn_value - b.burn_value)
          break
        case 'bc-desc':
          itemsClone.sort((a, b) => b.burn_value - a.burn_value)
          break
        case 'none':
        default:
          itemsClone = cloneDeep(this.items)
      }

      itemsClone = filterUnit(itemsClone, this.selectedUnits)
      itemsClone.forEach((item) => {
        units.push(splitUnitName(item.unit_name))
        earnValue.push(item.earn_value)
        burnValue.push(item.burn_value)
      })

      let datasets = []
      if (!this.hideEarn) {
        datasets.push({
          label: `${this.offerTypeNameCapital} gerado`,
          data: earnValue,
          backgroundColor: '#002040',
        })
      }
      datasets.push({
        label: `${this.offerTypeNameCapital} consumido`,
        data: burnValue,
        backgroundColor: '#94B300',
      })

      removeCommonElement(units)
      return {
        labels: units,
        datasets,
      }
    },
    width() {
      return this.chartData.datasets[0].data.length * 100 + 'px'
    },
  },
}
</script>

<style lang="scss" scoped>
.chart-container {
  position: relative;
  min-width: 100%;
}

.sorter {
  display: flex;
  gap: 42px;
  padding-left: 20px;
  user-select: none;

  :hover {
    opacity: 0.7;
    cursor: pointer;
  }

  .sorting-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    min-height: 36px;
  }

  .sorting-arrow {
    font-size: 24px;
  }
}
</style>
